import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { FaGlobe, FaChevronDown } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';

// Types
interface LanguageSelectorProps {
  isMobile?: boolean;
}

// Styled components
const LanguageButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: none;
  border: none;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  border-radius: var(--border-radius-md);
  font-size: 0.9rem;
  
  &:hover {
    background-color: var(--bg-secondary);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 0.75rem;
    border-radius: 0;
    margin: 0;
  }
`;

const LanguageIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  font-size: 1rem;
`;

const LanguageDropdown = styled.div<{ isOpen: boolean; isMobile?: boolean }>`
  position: ${props => props.isMobile ? 'relative' : 'absolute'};
  top: ${props => props.isMobile ? '0' : '100%'};
  right: ${props => props.isMobile ? '0' : '0'};
  min-width: ${props => props.isMobile ? '100%' : '150px'};
  background-color: var(--bg-primary);
  border-radius: var(--border-radius-md);
  box-shadow: ${props => props.isMobile ? 'none' : 'var(--shadow-md)'};
  padding: 0.5rem;
  z-index: 100;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.3s ease;
  margin-top: ${props => props.isMobile ? '0.5rem' : '0.25rem'};
  border: ${props => props.isMobile ? '1px solid var(--bg-secondary)' : 'none'};

  @media (max-width: 768px) {
    position: ${props => props.isMobile ? 'relative' : 'absolute'};
    width: 100%;
  }
`;

const LanguageOption = styled.button<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem;
  background: ${props => props.isSelected ? 'var(--bg-secondary)' : 'transparent'};
  border: none;
  border-radius: var(--border-radius-sm);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-primary);
  font-weight: ${props => props.isSelected ? '600' : '400'};
  
  &:hover {
    background-color: var(--bg-secondary);
  }
`;

const LanguageCode = styled.span`
  font-weight: 600;
  text-transform: uppercase;
  margin-right: 0.75rem;
  color: var(--primary-color);
`;

const LanguageName = styled.span`
  flex: 1;
`;

const LanguageContainer = styled.div<{ isMobile?: boolean }>`
  position: relative;
  display: ${props => props.isMobile ? 'block' : 'inline-block'};
  width: ${props => props.isMobile ? '100%' : 'auto'};
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

// Données des langues disponibles
const languages = [
  { code: 'fr', name: 'Français' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'en', name: 'English' }
];

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ isMobile = false }) => {
  const { currentLanguage: language, setLanguage, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  const handleLanguageChange = (langCode: 'fr' | 'nl' | 'en') => {
    setLanguage(langCode);
    setIsOpen(false);
  };
  
  // Fermer le dropdown lors d'un clic en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Trouver le nom de la langue actuelle
  const currentLanguage = languages.find(lang => lang.code === language)?.name || 'Français';
  
  return (
    <LanguageContainer isMobile={isMobile} ref={dropdownRef}>
      <LanguageButton onClick={toggleDropdown}>
        <LanguageIcon>
          <FaGlobe />
        </LanguageIcon>
        <span>{isMobile ? t('common.language') : currentLanguage}</span>
        <FaChevronDown size={12} />
      </LanguageButton>
      
      <LanguageDropdown isOpen={isOpen} isMobile={isMobile}>
        {languages.map(lang => (
          <LanguageOption 
            key={lang.code}
            isSelected={language === lang.code}
            onClick={() => handleLanguageChange(lang.code as 'fr' | 'nl' | 'en')}
          >
            <LanguageCode>{lang.code}</LanguageCode>
            <LanguageName>{lang.name}</LanguageName>
          </LanguageOption>
        ))}
      </LanguageDropdown>
    </LanguageContainer>
  );
};

export default LanguageSelector; 